import { PHASE } from "../../../../common/constants.ts";
import type { Phase } from "../../../../common/types.ts";

export type MobileNavigationContext = {
  hasOpenFreeAgency?: boolean;
  hasPendingNegotiations?: boolean;
  needsExpansionProtection?: boolean;
  phase: Phase;
  spectator?: boolean;
};

export type MobileNavigationEntry = {
  key: string;
  labelKey: string;
  defaultLabel: string;
  descriptionKey: string;
  defaultDescription: string;
  path?: string[];
  // M3-4: an entry can open an in-place sheet instead of navigating to a route.
  // The draft-picks page was retired into the DraftPicksSheet overlay, so its
  // market-hub entry carries this marker (and no path); the host renders it as a
  // sheet-opening button.
  sheet?: "draftPicks";
  available: boolean;
  unavailableKey?: string;
  defaultUnavailable?: string;
};

export type MobileNavigationGroup = {
  key: string;
  labelKey: string;
  defaultLabel: string;
  entries: MobileNavigationEntry[];
};

export const getPhaseRoute = (phase: Phase): string[] => {
  const urls: Record<Phase, string[]> = {
    [PHASE.EXPANSION_DRAFT]: ["protect_players"],
    [PHASE.FANTASY_DRAFT]: ["draft"],
    [PHASE.PRESEASON]: ["roster"],
    [PHASE.REGULAR_SEASON]: ["roster"],
    [PHASE.AFTER_TRADE_DEADLINE]: ["roster"],
    [PHASE.PLAYOFFS]: ["playoffs"],
    [PHASE.DRAFT_LOTTERY]: ["draft_lottery"],
    [PHASE.DRAFT]: ["draft"],
    [PHASE.AFTER_DRAFT]: ["draft"],
    [PHASE.RESIGN_PLAYERS]: ["negotiation"],
    [PHASE.FREE_AGENCY]: ["free_agents"],
  };

  return urls[phase];
};

const isDraftPhase = (phase: Phase) =>
  phase === PHASE.EXPANSION_DRAFT ||
  phase === PHASE.FANTASY_DRAFT ||
  phase === PHASE.DRAFT ||
  phase === PHASE.AFTER_DRAFT;

export const isFreeAgencyAvailable = (phase: Phase) =>
  !(
    (phase > PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.RESIGN_PLAYERS) ||
    phase === PHASE.FANTASY_DRAFT ||
    phase === PHASE.EXPANSION_DRAFT
  );

export const isTradeAvailable = (phase: Phase) =>
  phase < PHASE.AFTER_TRADE_DEADLINE && !isDraftPhase(phase);

const marketEntry = (
  key: string,
  path: string[],
  defaultLabel: string,
  defaultDescription: string,
  available = true,
  defaultUnavailable?: string,
): MobileNavigationEntry => ({
  available,
  defaultDescription,
  defaultLabel,
  defaultUnavailable,
  descriptionKey: `mobileNav.${key}.description`,
  key,
  labelKey: `mobileNav.${key}.label`,
  path,
  unavailableKey: defaultUnavailable ? `mobileNav.${key}.unavailable` : undefined,
});

// M3-4: the "My Picks" market entry opens the DraftPicksSheet in place rather
// than routing to the retired /draft_picks page, so it carries a sheet marker
// and no path.
const draftPicksSheetEntry = (defaultDescription: string): MobileNavigationEntry => ({
  available: true,
  defaultDescription,
  defaultLabel: "My Picks",
  descriptionKey: "mobileNav.draftPicks.description",
  key: "draftPicks",
  labelKey: "mobileNav.draftPicks.label",
  sheet: "draftPicks",
});

const getRecommendedMarketEntry = ({
  hasOpenFreeAgency,
  hasPendingNegotiations,
  needsExpansionProtection,
  phase,
}: MobileNavigationContext): MobileNavigationEntry => {
  if (needsExpansionProtection === true) {
    return marketEntry(
      "protectPlayers",
      ["protect_players"],
      "Protect Players",
      "Set protected players before the expansion draft proceeds.",
    );
  }

  if (phase === PHASE.DRAFT_LOTTERY) {
    return marketEntry(
      "draftLottery",
      ["draft_lottery"],
      "Draft Lottery",
      "Review lottery odds and results.",
    );
  }

  if (isDraftPhase(phase)) {
    return marketEntry(
      "draft",
      ["draft"],
      "Draft",
      "Make the current pick or advance to your next pick.",
    );
  }

  if (hasPendingNegotiations === true) {
    return marketEntry(
      "negotiation",
      ["negotiation"],
      "Negotiations",
      "Handle expiring contracts before free agency.",
    );
  }

  if (hasOpenFreeAgency || phase === PHASE.FREE_AGENCY) {
    return marketEntry(
      "freeAgents",
      ["free_agents"],
      "Free Agents",
      "Sign available players and fill roster needs.",
    );
  }

  if (
    (phase >= PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.PLAYOFFS) ||
    phase === PHASE.RESIGN_PLAYERS
  ) {
    return draftPicksSheetEntry("Review your draft assets while transactions are limited.");
  }

  return marketEntry("trade", ["trade"], "Trade", "Find a team and build an offer.");
};

export const getMarketNavigationGroups = (
  context: MobileNavigationContext,
): MobileNavigationGroup[] => {
  const { hasPendingNegotiations, phase } = context;
  const recommended = getRecommendedMarketEntry(context);
  const entries = [
    marketEntry(
      "trade",
      ["trade"],
      "Trade",
      "Swap players and picks with another team.",
      isTradeAvailable(phase),
      "Trades are closed in this phase.",
    ),
    marketEntry(
      "freeAgents",
      ["free_agents"],
      "Free Agents",
      "Sign available players and fill roster needs.",
      isFreeAgencyAvailable(phase),
      "Free agents cannot be signed in this phase.",
    ),
    marketEntry(
      "negotiation",
      ["negotiation"],
      "Negotiations",
      "Review player contract negotiations.",
      hasPendingNegotiations === true,
      "Negotiations are phase-specific.",
    ),
    marketEntry(
      "draft",
      ["draft"],
      "Draft",
      "Make picks or review the active draft board.",
      isDraftPhase(phase),
      "The draft is not active in this phase.",
    ),
    draftPicksSheetEntry("Review your draft assets."),
    marketEntry(
      "draftScouting",
      ["draft_scouting"],
      "Scouting",
      "Compare prospects before draft night.",
    ),
    marketEntry(
      "draftHistory",
      ["draft_history"],
      "Draft History",
      "Review earlier draft classes and selections.",
    ),
  ];
  const recommendedKeys = new Set([recommended.key]);

  return [
    {
      defaultLabel: "Recommended",
      entries: [recommended],
      key: "recommended",
      labelKey: "mobileNav.group.recommended",
    },
    {
      defaultLabel: "Available",
      entries: entries.filter((entry) => entry.available && !recommendedKeys.has(entry.key)),
      key: "available",
      labelKey: "mobileNav.group.available",
    },
    {
      defaultLabel: "Temporarily unavailable",
      entries: entries.filter((entry) => !entry.available),
      key: "unavailable",
      labelKey: "mobileNav.group.unavailable",
    },
  ].filter((group) => group.entries.length > 0);
};
